import { useState } from 'react'
import {
    ModerationBoundaryView,
    useGetAccountQuery,
    useGetClassifierDetailsQuery,
    useGetModerationBoundariesQuery,
    useGetPromptManagedClassifierIdsQuery
} from '../../api/client'
import { toastError } from '../Notification'
import SimpleLoader from '../SimpleLoader'
import Modal from '../Modal'
import EditModerationBoundary from './EditModerationBoundary'
import TemplateSelection from './ModerationBoundaryTemplateSelection'
import { ModerationTemplate } from './ModerationTemplates'
import ModerationQueueCard from './ModerationQueueCard'

export enum Queue {
    SAFE = 'SAFE',
    SUSPECT = 'SUSPECT',
    SEVERE = 'SEVERE'
}

type Props = {
    accountId: number
}

export default function ModerationContent(props: Props) {
    const { accountId } = props

    const [editOpen, setEditOpen] = useState(false)
    const [selectedBoundary, setSelectedBoundary] = useState<ModerationBoundaryView | undefined>()
    const [selectedTemplate, setSelectedTemplate] = useState<ModerationTemplate | undefined>()

    const { data: accountData } = useGetAccountQuery({
        variables: { accountId }
    })

    const { data, loading, refetch } = useGetModerationBoundariesQuery({
        variables: { accountId: accountId },
        fetchPolicy: 'network-only',
        onError: () => {
            toastError('Failed to get moderation boundaries')
        }
    })

    // Boundaries for a classifier with a live prompt are written by classifier
    // deployment and re-pointed at every go-live, so editing them here would be
    // undone by the next redeployment. They stay visible but read-only.
    const { data: promptManagedData } = useGetPromptManagedClassifierIdsQuery({
        onError: () => {
            toastError('Failed to check which classifiers are managed by a prompt')
        }
    })
    const promptManagedClassifierIds = new Set(
        promptManagedData?.getPromptManagedClassifierIds || []
    )

    const { data: classifierDetails } = useGetClassifierDetailsQuery()
    const classifierDetailsData = classifierDetails?.getClassifierDetails || []
    const sortedClassifierDetails = classifierDetailsData.slice().sort((a, b) => {
        if (a.displayName < b.displayName) {
            return -1
        }
        if (a.displayName > b.displayName) {
            return 1
        }
        return 0
    })

    const boundaries = data?.getModerationBoundaries

    const severeBoundaries = boundaries?.filter((boundary) => boundary.queue === Queue.SEVERE) || []

    const suspectBoundaries =
        boundaries?.filter((boundary) => boundary.queue === Queue.SUSPECT) || []

    const safeBoundaries = boundaries?.filter((boundary) => boundary.queue === Queue.SAFE) || []

    const account = accountData?.getAccount

    if (!account) {
        return <SimpleLoader loading={true} />
    }

    async function handleDeleteModerationBoundary(moderationBoundaryView: ModerationBoundaryView) {
        await refetch()
    }

    function filterExcludedClassifiers(
        classifierDetails: Array<any>,
        boundaries: ModerationBoundaryView[]
    ): Array<any> {
        return classifierDetails.filter(
            (classifierDetail) =>
                boundaries.findIndex(
                    (boundary) => boundary.classifier.id === classifierDetail.id
                ) === -1
        )
    }

    const queues = [
        {
            queue: Queue.SEVERE,
            title: 'Severe',
            dotClassName: 'bg-severe',
            boundaries: severeBoundaries,
            compareWith: selectedTemplate?.severe
        },
        {
            queue: Queue.SUSPECT,
            title: 'Suspect',
            dotClassName: 'bg-suspect',
            boundaries: suspectBoundaries,
            compareWith: selectedTemplate?.suspect
        },
        {
            queue: Queue.SAFE,
            title: 'Safe',
            dotClassName: 'bg-safe',
            boundaries: safeBoundaries,
            compareWith: selectedTemplate?.safe
        }
    ]

    return (
        <div className="flex flex-col gap-6">
            {/* Title, subtitle and the keywords link live in the page masthead
                (ModerationBoundariesPage). */}
            <TemplateSelection
                account={account}
                onTemplateApplied={function () {
                    refetch()
                }}
                selectedTemplate={selectedTemplate}
                onSetSelectedTemplate={setSelectedTemplate}
                classifierDetailsList={sortedClassifierDetails}
                promptManagedClassifierIds={promptManagedClassifierIds}
            />

            {/* Comparing adds a column to every table, which three across can't hold,
                so the cards go two across while a template is selected. — Claude */}
            <div
                className={`grid grid-cols-1 items-start gap-6 ${
                    selectedTemplate ? 'lg:grid-cols-2' : 'xl:grid-cols-3'
                }`}
            >
                {queues.map((q) => (
                    <ModerationQueueCard
                        key={q.queue}
                        queue={q.queue}
                        title={q.title}
                        dotClassName={q.dotClassName}
                        account={account}
                        boundaries={q.boundaries}
                        availableClassifiers={filterExcludedClassifiers(
                            sortedClassifierDetails,
                            q.boundaries
                        )}
                        loading={loading}
                        setSelectedBoundary={setSelectedBoundary}
                        setEditOpen={setEditOpen}
                        showCompare={!!selectedTemplate}
                        compareWith={q.compareWith || new Map()}
                        compareName={selectedTemplate?.displayName || ''}
                        onDelete={handleDeleteModerationBoundary}
                        onCreate={async () => {
                            await refetch()
                        }}
                        promptManagedClassifierIds={promptManagedClassifierIds}
                    />
                ))}
            </div>
            <Modal
                open={editOpen}
                onClose={() => {
                    setSelectedBoundary(undefined)
                    setEditOpen(false)
                }}
                title="Edit moderation boundary"
                size="sm"
            >
                <EditModerationBoundary
                    onClose={() => {
                        setEditOpen(false)
                    }}
                    onCloseAndSave={() => {
                        setEditOpen(false)
                        refetch()
                    }}
                    selectedBoundary={selectedBoundary}
                    onSelectedBoundaryUpdate={setSelectedBoundary}
                />
            </Modal>
        </div>
    )
}
